import { useMemo, useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";

interface RenderHtmlProps {
  html: string;
  className?: string;
}

/**
 * Renders HTML content inside a sandboxed iframe for extra security.
 * Auto-resizes to fit content height.
 */
export function RenderHtml({ html, className }: RenderHtmlProps) {
  const [iframeHeight, setIframeHeight] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeIdRef = useRef(
    `render-html-${Math.random().toString(36).slice(2, 11)}`,
  );

  const srcDoc = useMemo(() => {
    if (!html || html.trim().length === 0) {
      return null;
    }
    const sanitizedHtml = DOMPurify.sanitize(html);
    return generateSrcDoc(sanitizedHtml, iframeIdRef.current);
  }, [html]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Sandboxed iframes without allow-same-origin post with origin "null".
      // Combined with the source check below, this confirms the message came
      // from our own iframe's content window and not from another frame on
      // the page that happens to know the id.
      if (event.origin !== "null") {
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      if (
        event.data?.type === "resize" &&
        event.data?.id === iframeIdRef.current &&
        typeof event.data?.height === "number"
      ) {
        setIframeHeight(event.data.height);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  if (!srcDoc) {
    return null;
  }

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcDoc}
      title="HTML content"
      className={className}
      style={{
        width: "100%",
        height: iframeHeight > 0 ? `${iframeHeight}px` : "auto",
        minHeight: "1em",
        border: "none",
        display: "block",
        overflow: "hidden",
      }}
      sandbox="allow-scripts"
      loading="lazy"
    />
  );
}

function generateSrcDoc(sanitizedHtml: string, iframeId: string): string {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    /* Reset and base styles */
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 0.875rem;
      line-height: 1.625;
      color: #374151;
      background: transparent;
      overflow: hidden;
    }

    /* Typography styles matching Tailwind prose-sm */
    h2 {
      font-size: 1.25rem;
      font-weight: 600;
      color: #111827;
      margin-top: 1.5rem;
      margin-bottom: 0.75rem;
      line-height: 1.4;
    }

    h2:first-child {
      margin-top: 0;
    }

    h3 {
      font-size: 1.125rem;
      font-weight: 600;
      color: #1f2937;
      margin-top: 1.25rem;
      margin-bottom: 0.5rem;
      line-height: 1.4;
    }

    h3:first-child {
      margin-top: 0;
    }

    p {
      margin-bottom: 0.75rem;
    }

    p:last-child {
      margin-bottom: 0;
    }

    ul, ol {
      margin-bottom: 0.75rem;
      padding-left: 1.5rem;
    }

    ul:last-child, ol:last-child {
      margin-bottom: 0;
    }

    ul {
      list-style-type: disc;
    }

    ol {
      list-style-type: decimal;
    }

    li {
      margin-bottom: 0.25rem;
    }

    li:last-child {
      margin-bottom: 0;
    }

    strong {
      font-weight: 600;
      color: #1f2937;
    }

    em {
      font-style: italic;
    }

    /* Links */
    a {
      color: #2563eb;
      text-decoration: underline;
    }

    a:hover {
      color: #1d4ed8;
    }

    /* Horizontal rule */
    hr {
      border: none;
      border-top: 1px solid #e5e7eb;
      margin: 1rem 0;
    }

    /* Blockquote */
    blockquote {
      border-left: 3px solid #d1d5db;
      padding-left: 1rem;
      margin: 0.75rem 0;
      color: #4b5563;
      font-style: italic;
    }
  </style>
</head>
<body>
  <div id="content">${sanitizedHtml}</div>
  <script>
    (function() {
      var iframeId = '${iframeId}';
      var content = document.getElementById('content');

      function sendHeight() {
        var height = document.body.scrollHeight;
        window.parent.postMessage({
          type: 'resize',
          id: iframeId,
          height: height
        }, '*');
      }

      // Initial height after a small delay for fonts to load
      setTimeout(sendHeight, 50);

      // Watch for changes with ResizeObserver
      if (typeof ResizeObserver !== 'undefined') {
        var observer = new ResizeObserver(sendHeight);
        observer.observe(content);
      }

      // Also send on window load (for fonts, images)
      window.addEventListener('load', sendHeight);

      // Send again after fonts are likely loaded
      setTimeout(sendHeight, 500);
    })();
  </script>
</body>
</html>`;
}
